import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { 
  Plane, 
  Car, 
  Clock, 
  Shield, 
  Phone, 
  Mail, 
  MapPin,
  MessageSquare,
  Users,
  Menu,
  X,
  ChevronRight,
  Star,
  Sparkles,
  Bot,
  Zap,
  Loader2
} from 'lucide-react';

const LandingPage = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Booking form state
  const [formData, setFormData] = useState({
    pickup_location: '',
    dropoff_location: '',
    date_time: '',
    phone_number: '',
    return_journey: false,
    return_date_time: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pickup_location || !formData.dropoff_location || !formData.date_time || !formData.phone_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormSubmitting(true);

    try {
      const dateTime = new Date(formData.date_time);
      const journeyDate = dateTime.toISOString().split('T')[0];
      const pickupTime = dateTime.toTimeString().slice(0, 5);

      const bookingData = {
        full_name: user?.name || 'Guest',
        contact_number: formData.phone_number,
        email: user?.email || 'guest@neoride.com',
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        journey_date: journeyDate,
        pickup_time: pickupTime,
        passengers: 1,
        additional_requirements: formData.return_journey ? `Return journey requested for ${formData.return_date_time}` : null,
        is_airport_transfer: formData.pickup_location.toLowerCase().includes('airport') || formData.dropoff_location.toLowerCase().includes('airport'),
        is_guest: !user,
        user_id: user?.user_id || null
      };

      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) throw new Error('Booking failed');

      const booking = await response.json();
      
      if (formData.return_journey && formData.return_date_time) {
        const returnDateTime = new Date(formData.return_date_time);
        const returnDate = returnDateTime.toISOString().split('T')[0];
        const returnTime = returnDateTime.toTimeString().slice(0, 5);

        await fetch(`${API}/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...bookingData,
            pickup_location: formData.dropoff_location,
            dropoff_location: formData.pickup_location,
            journey_date: returnDate,
            pickup_time: returnTime,
            additional_requirements: `Return journey for booking ${booking.booking_id}`
          })
        });
      }

      toast.success('Booking submitted!', {
        description: `Reference: ${booking.booking_id}. Check your email for confirmation.`
      });

      setFormData({
        pickup_location: '',
        dropoff_location: '',
        date_time: '',
        phone_number: '',
        return_journey: false,
        return_date_time: ''
      });

    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Professional Drivers",
      description: "All our drivers are professional, vetted and experienced"
    },
    {
      icon: <Plane className="w-6 h-6" />,
      title: "Airport Transfers",
      description: "Specialised airport pickup and drop-off with flight monitoring"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 Online Booking",
      description: "Book your journey online anytime, day or night"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Quick Confirmation",
      description: "Get quick responses and fare confirmation from our team"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-slate-900">NeoRide</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/book" className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors" data-testid="nav-book">
                Book Online
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors" data-testid="nav-dashboard">
                    My Bookings
                  </Link>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={logout}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="nav-logout-btn"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={login}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
                  size="sm"
                  data-testid="nav-login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/book" className="block text-slate-700 hover:text-violet-600 py-2" onClick={() => setMobileMenuOpen(false)}>
                Book Online
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="block text-slate-700 hover:text-violet-600 py-2" onClick={() => setMobileMenuOpen(false)}>
                    My Bookings
                  </Link>
                  <button onClick={logout} className="block text-slate-700 hover:text-violet-600 py-2 w-full text-left">
                    Logout
                  </button>
                </>
              ) : (
                <button onClick={login} className="block text-violet-600 hover:text-violet-700 py-2 w-full text-left font-medium">
                  Sign In with Google
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        {/* AI-themed gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-white to-indigo-50" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238b5cf6' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-violet-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 border border-amber-200 text-sm text-amber-700">
                <Clock className="w-4 h-4" />
                Awaiting Licence
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter text-slate-900 leading-tight">
                Professional<br />
                <span className="gradient-text">Private Hire</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-md leading-relaxed">
                Professional drivers, 24/7 online booking. 
                Reliable service across Wolverhampton and airport transfers.
              </p>
              
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="flex items-center gap-2 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-full">
                  <Shield className="w-4 h-4" /> Professional Drivers
                </span>
                <span className="flex items-center gap-2 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-full">
                  <Clock className="w-4 h-4" /> 24/7 Available
                </span>
                <span className="flex items-center gap-2 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-full font-medium">
                  <MessageSquare className="w-4 h-4" /> Online Booking Only
                </span>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => document.getElementById('hero-booking-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 px-8 py-6 text-lg rounded-full ai-glow"
                  data-testid="hero-book-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Book Online Now
                </Button>
              </div>
            </div>

            {/* Right Content - Booking Form */}
            <div className="animate-fade-in-up stagger-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md mx-auto lg:ml-auto" data-testid="hero-booking-form">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Book Your Ride</h2>
                
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Pickup Location</Label>
                    <Input
                      name="pickup_location"
                      value={formData.pickup_location}
                      onChange={handleFormChange}
                      placeholder="Pickup Location"
                      className="border-slate-300"
                      required
                      data-testid="hero-input-pickup"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Drop-off Location</Label>
                    <Input
                      name="dropoff_location"
                      value={formData.dropoff_location}
                      onChange={handleFormChange}
                      placeholder="Drop-off Location"
                      className="border-slate-300"
                      required
                      data-testid="hero-input-dropoff"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Date & Time</Label>
                    <Input
                      type="datetime-local"
                      name="date_time"
                      value={formData.date_time}
                      onChange={handleFormChange}
                      min={getMinDateTime()}
                      className="border-slate-300"
                      required
                      data-testid="hero-input-datetime"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleFormChange}
                      placeholder="Phone Number"
                      className="border-slate-300"
                      required
                      data-testid="hero-input-phone"
                    />
                  </div>

                  <div className="flex items-center gap-3 py-2">
                    <Checkbox
                      id="hero_return_journey"
                      checked={formData.return_journey}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, return_journey: checked }))}
                      className="border-violet-600 data-[state=checked]:bg-violet-600"
                    />
                    <Label htmlFor="hero_return_journey" className="text-sm font-medium text-slate-700 cursor-pointer">
                      Book Return Journey
                    </Label>
                  </div>
                  
                  {formData.return_journey && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Return Date & Time</Label>
                      <Input
                        type="datetime-local"
                        name="return_date_time"
                        value={formData.return_date_time}
                        onChange={handleFormChange}
                        min={formData.date_time || getMinDateTime()}
                        className="border-slate-300"
                        required={formData.return_journey}
                        data-testid="hero-input-return-datetime"
                      />
                    </div>
                  )}

                  <Button 
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white py-6 text-lg font-semibold rounded-xl"
                    data-testid="hero-submit-btn"
                  >
                    {formSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'BOOK NOW'
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <p className="text-slate-500 text-sm">Online Booking Only - No Phone Bookings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600 mb-4">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-slate-900">
              Smart Booking, Superior Service
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 space-y-4 border border-slate-200 hover:border-violet-300 hover:shadow-lg hover:shadow-violet-100 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center text-violet-600">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-medium text-slate-900">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Airport Transfers Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="airport-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600">Speciality</p>
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-slate-900">
                Airport Transfers
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We specialise in airport transfers with flight monitoring to ensure punctual 
                pickups. Whether you're heading to Birmingham, Manchester, Heathrow, or any 
                other UK airport, we've got you covered.
              </p>
              <ul className="space-y-3">
                {['Flight time monitoring', 'Meet & greet available', 'Fixed prices - no surprises', 'All UK airports'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <Star className="w-4 h-4 text-violet-600" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/book')}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 rounded-full px-8"
                data-testid="airport-book-btn"
              >
                Book Airport Transfer
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1678796780807-339f82de2d54" 
                alt="Airport Transfer"
                className="rounded-2xl w-full h-[400px] object-cover shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                    <Plane className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Flight Tracking</p>
                    <p className="text-xs text-slate-500">We monitor your arrival</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-violet-600 to-indigo-700" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white mb-6">
            Ready to Book Your Journey?
          </h2>
          <p className="text-violet-100 mb-8 max-w-2xl mx-auto">
            Book online 24/7. Professional drivers, reliable service.
            Simply tell us where you want to go and we'll handle the rest.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => navigate('/book')}
              className="bg-white text-violet-700 hover:bg-violet-50 px-8 py-6 text-lg rounded-full"
              data-testid="cta-book-btn"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Book Online Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-12 px-4 sm:px-6 lg:px-8 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Car className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-semibold text-slate-900">NeoRide</span>
              </div>
              <p className="text-sm text-slate-500">
                Professional private hire service. 
                Online booking available 24/7 across Wolverhampton.
              </p>
              <div className="flex items-center gap-2 text-sm font-medium text-violet-600">
                <Clock className="w-4 h-4" />
                Online Booking: 24/7
              </div>
            </div>
            
            <div>
              <h4 className="text-slate-900 font-medium mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/book" className="text-sm text-slate-500 hover:text-violet-600 transition-colors">Book Online</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-slate-900 font-medium mb-4">Contact</h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-slate-500">
                  <Mail className="w-4 h-4" /> neorideai@gmail.com
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-500">
                  <MapPin className="w-4 h-4 mt-0.5" /> 
                  <span>28 Cleveland Street,<br />Wolverhampton, WV1 3HT</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-200 mt-12 pt-8 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} NeoRide. All rights reserved. | Online Booking Only</p>
            <p className="mt-2">Created by: <a href="https://wktechexpert.com/" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700 transition-colors">wktechexpert.com</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
